import { clsx } from 'clsx';
import { Handshake, Truck } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import {
  useVersandarten,
  CODE_ABHOLUNG,
  CODE_VERSAND_SAMMEL,
  istVersandCode
} from '@/hooks/useVersandarten';

export type Zustellweg = 'abholung' | 'versand';

type Props = {
  /** Die Pflichtwahl. `null`, solange niemand gewaehlt hat. */
  weg: Zustellweg | null;
  /** Die optionale Praezisierung bei Versand. */
  versandartId: number | null;
  onWeg: (weg: Zustellweg) => void;
  onVersandart: (id: number | null) => void;
};

/**
 * Die Weiche beim Erfassen: Abholung oder Versand (E20).
 *
 * WARUM ZWEI STUFEN
 *   Am Tresen steht eine Sache verlaesslich fest — holt die Kundin ab,
 *   oder geht es raus? Ob DPD, DHL oder Briefsendung weiss erst, wer
 *   spaeter packt. Die genaue Art hier zu verlangen hiesse, eine Angabe
 *   zu erzwingen, die zum Zeitpunkt der Frage niemand hat; erzwungene
 *   Angaben werden geraten, und geratene Angaben sind schlechter als
 *   keine.
 *
 *   Deshalb: oben die Pflicht, darunter die Kuer. Leer lassen ist
 *   ausdruecklich erlaubt und heisst schlicht "Versand".
 */
export function ZustellwegPicker({ weg, versandartId, onWeg, onVersandart }: Props) {
  const q = useVersandarten();

  if (q.isPending) return <Spinner />;
  if (q.isError || !q.data) {
    return <p className="text-sm text-coral">Versandarten konnten nicht geladen werden.</p>;
  }

  // Die Praezisierungen sind alles ausser Abholung und dem Sammelbegriff
  // selbst — "Versand" steht schon oben als Knopf.
  const genauere = q.data.items.filter(
    (v) => istVersandCode(v.code) && v.code !== CODE_VERSAND_SAMMEL
  );
  const hatAbholung = q.data.items.some((v) => v.code === CODE_ABHOLUNG);

  return (
    <div className="space-y-3">
      {/* Zwei grosse Knoepfe. Kein Hover-only: die Wahl ist am
          `aria-pressed` und an der Fuellung zu sehen, nicht daran, wo
          der Zeiger steht — auf dem Tablet gibt es keinen Zeiger. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <WegKnopf
          aktiv={weg === 'abholung'}
          onClick={() => onWeg('abholung')}
          Icon={Handshake}
          titel="Abholung"
          unterzeile="Die Kundin holt im Laden ab"
          gesperrt={!hatAbholung}
        />
        <WegKnopf
          aktiv={weg === 'versand'}
          onClick={() => onWeg('versand')}
          Icon={Truck}
          titel="Versand"
          unterzeile="Geht raus — wie, kann später"
        />
      </div>

      {weg === 'versand' && genauere.length > 0 && (
        <div className="rounded-md border border-line bg-cream-warm p-3">
          <p className="mb-2 text-xs text-ink-soft">
            Genaue Versandart — kannst du auch später beim Packen setzen.
          </p>
          <div className="flex flex-wrap gap-2">
            {/* "Noch offen" ist eine echte Wahl und steht deshalb als
                Knopf da, nicht als blosses Nichts-Anklicken. Wer nichts
                waehlt, soll sehen, dass das in Ordnung ist. */}
            <ArtKnopf
              aktiv={versandartId === null}
              onClick={() => onVersandart(null)}
              label="Noch offen"
            />
            {genauere.map((v) => (
              <ArtKnopf
                key={v.id}
                aktiv={versandartId === v.id}
                onClick={() => onVersandart(v.id)}
                label={v.label}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WegKnopf({
  aktiv,
  onClick,
  Icon,
  titel,
  unterzeile,
  gesperrt = false
}: {
  aktiv: boolean;
  onClick: () => void;
  Icon: typeof Handshake;
  titel: string;
  unterzeile: string;
  gesperrt?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={gesperrt}
      aria-pressed={aktiv}
      className={clsx(
        'flex min-h-[64px] items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors',
        gesperrt && 'cursor-not-allowed opacity-50',
        aktiv
          ? 'border-akzent bg-akzent text-cream'
          : 'border-line bg-cream text-ink-soft hover:border-akzent hover:text-akzent'
      )}
    >
      <Icon className="h-6 w-6 shrink-0" />
      <span className="min-w-0">
        <span className="block text-base font-medium">{titel}</span>
        <span className={clsx('block text-xs', aktiv ? 'text-cream/80' : 'text-ink-faint')}>
          {unterzeile}
        </span>
      </span>
    </button>
  );
}

function ArtKnopf({
  aktiv,
  onClick,
  label
}: {
  aktiv: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktiv}
      className={clsx(
        'min-h-[44px] rounded-md border px-3 py-1.5 text-sm transition-colors',
        aktiv
          ? 'border-akzent bg-akzent-sanft text-akzent-stark'
          : 'border-line bg-cream text-ink-soft hover:border-akzent hover:text-akzent'
      )}
    >
      {label}
    </button>
  );
}
